// Pricing Strategy feedback questionnaire (spec section 25) - a config
// object consumed by the reusable <StrategyFeedback /> component (spec
// section 26). Future strategy types (Negotiation, Marketing, Offer,
// Inspection) provide their own config with this same shape and reuse the
// same component/submission/reward logic.
//
// A function of `isBuyer` (not a static object) - it used to hard-code
// "seller" in the comfortableShowingClient question regardless of which
// side the transaction represents, so an agent representing a Buyer was
// asked "would you show this to your seller?" Representation-aware per the
// same principle as the rest of Intelligent Strategies (see
// functions/strategyCredits/representation.js) - client is "Buyer" or
// "Seller", never a generic/neutral placeholder shown to the wrong side.

export function buildPricingFeedbackConfig(isBuyer) {
  const clientLabel = isBuyer ? "buyer" : "seller";
  return {
    strategyType: "pricing",
    questions: [
    {
      id: "priceAccuracy",
      type: "radio",
      required: true,
      prompt: "How accurate does the recommended price/value range feel?",
      options: ["Too low", "Slightly low", "About right", "Slightly high", "Too high", "Not sure"],
    },
    {
      id: "compsQuality",
      type: "radio",
      required: true,
      prompt: "Were the comparable properties appropriate?",
      options: ["Very good", "Mostly good", "Some good / some poor", "Mostly poor", "No useful comps", "Not sure"],
      followUp: {
        id: "compsIssues",
        type: "text",
        prompt: "Which comps should not have been used, and why?",
        showWhen: (answers) => ["Some good / some poor", "Mostly poor", "No useful comps"].includes(answers.compsQuality),
      },
    },
    {
      id: "missingComps",
      type: "radio",
      required: false,
      prompt: "Were important comparable sales missing?",
      options: ["No", "Yes", "Not sure"],
      followUp: {
        id: "missingCompsAddresses",
        type: "text",
        prompt: "Enter the address(es), if known.",
        showWhen: (answers) => answers.missingComps === "Yes",
      },
    },
    {
      id: "issuesIdentified",
      type: "checkboxes",
      required: false,
      prompt: "What did TransActioner get wrong or miss?",
      options: [
        "Above-grade square footage", "Finished basement", "Bedroom layout", "Bathrooms", "Property type",
        "Condition / updates", "Lot / location", "Busy road / noise", "Airport / flight noise", "Views / open space",
        "Garage / parking", "HOA", "Prior sale history", "Market conditions", "Active competition", "Other",
      ],
      followUp: {
        id: "issuesOtherText",
        type: "text",
        prompt: "Tell us more",
        showWhen: (answers) => (answers.issuesIdentified || []).includes("Other"),
      },
    },
    {
      id: "comfortableShowingClient",
      type: "radio",
      required: true,
      prompt: `Would you feel comfortable showing this analysis to your ${clientLabel}?`,
      options: [
        "Yes, as-is",
        "Yes, with minor edits",
        "No, it needs significant changes",
        `I would not normally show this type of analysis to a ${clientLabel}`,
      ],
      followUp: {
        id: "whatWouldChange",
        type: "text",
        prompt: "What would you change?",
        showWhen: (answers) => ["Yes, with minor edits", "No, it needs significant changes"].includes(answers.comfortableShowingClient),
      },
    },
    {
      id: "priceRecommendation",
      type: "priceRange",
      required: false,
      prompt: "What price would YOU recommend?",
      fields: [
        { id: "recommendedListPrice", label: isBuyer ? "Recommended Offer Price" : "Recommended List Price" },
        { id: "estimatedMarketValueLow", label: "Estimated Market Value (Low)" },
        { id: "estimatedMarketValueHigh", label: "Estimated Market Value (High)" },
      ],
    },
    {
      id: "additionalComments",
      type: "text",
      required: false,
      prompt: "Anything else we should know?",
    },
    ],
  };
}
